import { ResendOrderOfferResponse } from "codegen";
import { useState } from "react";
import styled from "styled-components";
import THEME from "theme";

import { faPaperPlane } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { Title } from "components/atoms/Typography";
import Warning from "components/molecules/Warning";
import ModalButton, { useModalButtonContext } from "components/organisms/ModalButton";
import FormButton from "components/organisms/form/FormButton";
import { useStrombestillingApiMutation } from "hooks/useStrombestillingApi";
import { emailUnsubscribedMessage } from "pages/Strombestilling/orderoffers/orderOfferUtils";

type ResendStatus = "idle" | "sending" | "success" | "emailUnsubscribed" | "error";

export function ResendOrderOfferButton({ offerId, sendSms }: { readonly offerId: string; readonly sendSms: boolean }) {
	return (
		<StyledModalButton
			style={{ fontSize: "20px" }}
			className="noStyle"
			title="Send bestillingslenke på nytt"
			icon={<FontAwesomeIcon color={THEME.colors.primary} icon={faPaperPlane} />}
		>
			<ResendModalContent offerId={offerId} sendSms={sendSms} />
		</StyledModalButton>
	);
}

function ResendModalContent({ offerId, sendSms }: { readonly offerId: string; readonly sendSms: boolean }) {
	const { close } = useModalButtonContext();
	const [status, setStatus] = useState<ResendStatus>("idle");
	const isDone = status === "success" || status === "emailUnsubscribed";

	const mutation = useStrombestillingApiMutation("/orderoffer/resend", ["orderoffers"], "POST");

	async function resend() {
		setStatus("sending");
		try {
			const response = await mutation.mutateAsync({ orderOfferId: offerId });
			if (!response.ok) {
				setStatus("error");
				return;
			}
			const body: ResendOrderOfferResponse = await response.json();
			setStatus(body.epostReservert ? "emailUnsubscribed" : "success");
		} catch {
			setStatus("error");
		}
	}

	return (
		<ModalContent>
			<Title>Send bestillingslenke på nytt</Title>
			{status === "success" && <p>Bestillingslenken er sendt til kunden på nytt.</p>}
			{status === "emailUnsubscribed" && (
				<MessageBox>
					<Warning warningMessage={emailUnsubscribedMessage(sendSms)} />
				</MessageBox>
			)}
			{!isDone && <p>Er du sikker? Kunden vil motta bestillingslenken på nytt.</p>}
			{status === "error" && <ErrorText>Klarte ikke å sende bestillingslenken. Prøv igjen senere.</ErrorText>}
			{!isDone && (
				<FormButton
					style={{ marginRight: "1rem" }}
					loading={status === "sending"}
					onClick={(e) => {
						e.stopPropagation();
						resend();
					}}
					label="Send på nytt"
				/>
			)}
			<FormButton
				onClick={(e) => {
					close();
					e.stopPropagation();
				}}
				label={isDone ? "Lukk" : "Avbryt"}
			/>
		</ModalContent>
	);
}

const StyledModalButton = styled(ModalButton)`
	padding: 0 0.5rem;
	border-radius: 1rem;
`;

const ModalContent = styled.div`
	padding: 1rem;
`;

const MessageBox = styled.div`
	margin: 1rem 0;
`;

const ErrorText = styled.p`
	color: var(--varsel-roed);
`;
